/**
 * HTTP API Client for frontend communication with the backend API
 *
 * Features:
 * - Typed GET, POST, PUT, DELETE, PATCH methods
 * - Automatic JSON parsing
 * - Authentication token handling
 * - Request/response/error interceptors
 * - User-friendly error messages
 * - Request timeout support
 * - FormData support for file uploads
 */

import {
  type HttpMethod,
  type RequestConfig,
  type ApiResponse,
  type ApiErrorResponse,
  ApiError,
  type RequestInterceptor,
  type ResponseInterceptor,
  type ErrorInterceptor,
  type Interceptors,
  type ApiClientConfig,
} from '../types/api';
import type { Receipt, ReceiptFormData } from '../types/expense-tracker';

/**
 * Default timeout for requests in milliseconds
 */
const DEFAULT_TIMEOUT = 30000;

/**
 * Build URL with query parameters
 */
function buildUrl(baseUrl: string, path: string, params?: Record<string, string | number | boolean | undefined>): string {
  // Normalize the base URL and path
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  let url = `${normalizedBase}${normalizedPath}`;

  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    }
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  return url;
}

/**
 * Parse response body as JSON or return null for empty responses
 */
async function parseResponseBody<T>(response: Response): Promise<T | null> {
  const contentType = response.headers.get('content-type');
  const contentLength = response.headers.get('content-length');

  // Handle empty responses
  if (contentLength === '0' || response.status === 204) {
    return null;
  }

  // Parse JSON if content-type is application/json
  if (contentType?.includes('application/json')) {
    const text = await response.text();
    if (!text.trim()) {
      return null;
    }
    return JSON.parse(text) as T;
  }

  // Return null for non-JSON responses
  return null;
}

/**
 * Create a timeout signal
 */
function createTimeoutSignal(timeout: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), timeout);
  return controller.signal;
}

/**
 * Combine multiple abort signals
 */
function combineSignals(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();

  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      break;
    }
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  }

  return controller.signal;
}

/**
 * API Client class for making HTTP requests to the backend
 */
export class ApiClient {
  private readonly config: Required<Pick<ApiClientConfig, 'baseUrl' | 'timeout'>> & ApiClientConfig;
  private readonly interceptors: Interceptors = {
    request: [],
    response: [],
    error: [],
  };

  constructor(config: ApiClientConfig) {
    this.config = {
      ...config,
      timeout: config.timeout ?? DEFAULT_TIMEOUT,
    };
  }

  /**
   * Add a request interceptor
   * Returns a function to remove the interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): () => void {
    this.interceptors.request.push(interceptor);
    return () => {
      const index = this.interceptors.request.indexOf(interceptor);
      if (index > -1) {
        this.interceptors.request.splice(index, 1);
      }
    };
  }

  /**
   * Add a response interceptor
   * Returns a function to remove the interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): () => void {
    this.interceptors.response.push(interceptor);
    return () => {
      const index = this.interceptors.response.indexOf(interceptor);
      if (index > -1) {
        this.interceptors.response.splice(index, 1);
      }
    };
  }

  /**
   * Add an error interceptor
   * Returns a function to remove the interceptor
   */
  addErrorInterceptor(interceptor: ErrorInterceptor): () => void {
    this.interceptors.error.push(interceptor);
    return () => {
      const index = this.interceptors.error.indexOf(interceptor);
      if (index > -1) {
        this.interceptors.error.splice(index, 1);
      }
    };
  }

  /**
   * Execute request interceptors
   */
  private async executeRequestInterceptors(
    url: string,
    config: RequestInit
  ): Promise<{ url: string; config: RequestInit }> {
    let result = { url, config };

    for (const interceptor of this.interceptors.request) {
      result = await interceptor(result.url, result.config);
    }

    return result;
  }

  /**
   * Execute response interceptors
   */
  private async executeResponseInterceptors(
    response: Response,
    request: { url: string; config: RequestInit }
  ): Promise<Response> {
    let result = response;

    for (const interceptor of this.interceptors.response) {
      result = await interceptor(result, request);
    }

    return result;
  }

  /**
   * Execute error interceptors
   */
  private async executeErrorInterceptors(
    error: ApiError,
    request: { url: string; config: RequestInit }
  ): Promise<ApiError> {
    let result = error;

    for (const interceptor of this.interceptors.error) {
      result = await interceptor(result, request);
    }

    return result;
  }

  /**
   * Core request method
   */
  async request<T>(
    method: HttpMethod,
    path: string,
    config: RequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const url = buildUrl(this.config.baseUrl, path, config.params);
    const timeout = config.timeout ?? this.config.timeout;

    // Build headers
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      ...this.config.defaultHeaders,
      ...config.headers,
    };

    // Add content-type for requests with body (skip for FormData)
    if (config.body !== undefined && !headers['Content-Type'] && !(config.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Get auth token if available
    if (this.config.getAuthToken) {
      const token = await this.config.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // Build fetch config
    let fetchConfig: RequestInit = {
      method,
      headers,
      credentials: config.credentials ?? 'same-origin',
    };

    // Add body for non-GET requests
    if (config.body !== undefined && method !== 'GET') {
      // Handle FormData (don't stringify, let browser set Content-Type)
      if (config.body instanceof FormData) {
        fetchConfig.body = config.body;
        // Remove Content-Type header to let browser set it with boundary
        delete (fetchConfig.headers as Record<string, string>)['Content-Type'];
      } else {
        fetchConfig.body = JSON.stringify(config.body);
      }
    }

    // Create combined abort signal
    const signals: AbortSignal[] = [createTimeoutSignal(timeout)];
    if (config.signal) {
      signals.push(config.signal);
    }
    fetchConfig.signal = combineSignals(signals);

    // Execute request interceptors
    const interceptedRequest = await this.executeRequestInterceptors(url, fetchConfig);
    const finalUrl = interceptedRequest.url;
    fetchConfig = interceptedRequest.config;

    try {
      let response = await fetch(finalUrl, fetchConfig);

      // Execute response interceptors
      response = await this.executeResponseInterceptors(response, { url: finalUrl, config: fetchConfig });

      // Handle error responses
      if (!response.ok) {
        let errorResponse: ApiErrorResponse | undefined;

        try {
          const parsed = await parseResponseBody<ApiErrorResponse>(response);
          errorResponse = parsed ?? undefined;
        } catch {
          // Ignore parse errors for error responses
        }

        // Backend may return { error: "..." } or { message: "..." } - handle both
        const errorMessage = errorResponse?.message
          || (errorResponse as unknown as { error?: string })?.error
          || response.statusText;
        const apiError = new ApiError(
          errorMessage,
          response.status,
          response.statusText,
          errorResponse
        );

        // Handle 401 unauthorized
        if (response.status === 401 && this.config.onUnauthorized) {
          await this.config.onUnauthorized();
        }

        // Execute error interceptors
        const finalError = await this.executeErrorInterceptors(apiError, { url: finalUrl, config: fetchConfig });
        throw finalError;
      }

      // Parse successful response
      const data = await parseResponseBody<T>(response);

      return {
        data: data as T,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error) {
      // Handle abort errors (timeout or manual cancellation)
      if (error instanceof DOMException && error.name === 'AbortError') {
        const apiError = new ApiError(
          'Request was cancelled or timed out',
          0,
          'Aborted'
        );
        throw await this.executeErrorInterceptors(apiError, { url: finalUrl, config: fetchConfig });
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const apiError = new ApiError(
          'Network error. Please check your connection.',
          0,
          'NetworkError'
        );
        throw await this.executeErrorInterceptors(apiError, { url: finalUrl, config: fetchConfig });
      }

      // Re-throw ApiError instances
      if (error instanceof ApiError) {
        throw error;
      }

      // Wrap unknown errors
      const apiError = new ApiError(
        error instanceof Error ? error.message : 'An unexpected error occurred',
        0,
        'UnknownError'
      );
      throw await this.executeErrorInterceptors(apiError, { url: finalUrl, config: fetchConfig });
    }
  }

  /**
   * Perform a GET request
   */
  async get<T>(path: string, config?: Omit<RequestConfig, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, config);
  }

  /**
   * Perform a POST request
   */
  async post<T>(path: string, body?: unknown, config?: Omit<RequestConfig, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, { ...config, body });
  }

  /**
   * Perform a PUT request
   */
  async put<T>(path: string, body?: unknown, config?: Omit<RequestConfig, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, { ...config, body });
  }

  /**
   * Perform a PATCH request
   */
  async patch<T>(path: string, body?: unknown, config?: Omit<RequestConfig, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', path, { ...config, body });
  }

  /**
   * Perform a DELETE request
   */
  async delete<T>(path: string, config?: Omit<RequestConfig, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path, config);
  }
}

/**
 * Create a pre-configured API client instance for the application
 */
export function createApiClient(config?: Partial<ApiClientConfig>): ApiClient {
  return new ApiClient({
    baseUrl: '/api',
    timeout: DEFAULT_TIMEOUT,
    ...config,
  });
}

/**
 * Default API client instance
 * This will be configured with authentication when the auth service is set up
 */
export const apiClient = createApiClient();

/**
 * Receipt API methods
 */
export const receiptApi = {
  /**
   * Get a single receipt by ID
   * @param receiptId - The receipt ID to fetch
   * @returns Promise with the receipt data
   */
  async get(receiptId: string): Promise<ApiResponse<Receipt>> {
    return apiClient.get<Receipt>(`/receipts/${receiptId}`);
  },

  /**
   * Update receipt data
   * @param receiptId - The receipt ID to update
   * @param data - The receipt form data to update
   * @returns Promise with the updated receipt
   */
  async update(receiptId: string, data: ReceiptFormData): Promise<ApiResponse<Receipt>> {
    return apiClient.put<Receipt>(`/receipts/${receiptId}`, data);
  },

  /**
   * Get receipt image URL
   * @param receiptId - The receipt ID
   * @returns The URL to fetch the receipt image
   */
  getImageUrl(receiptId: string): string {
    return `/api/receipts/${receiptId}/image`;
  }
};

// ============================================================================
// QuickBooks Bulk Sync API
// ============================================================================

/**
 * Result of a single report in a bulk sync operation
 */
export interface BulkSyncItemResult {
  id: string;
  success: boolean;
  quickbooksBillId?: string;
  error?: string;
  skipped?: boolean;
  skipReason?: string;
}

/**
 * Summary counts for a bulk sync operation
 */
export interface BulkSyncSummary {
  total: number;
  succeeded: number;
  failed: number;
  skipped: number;
}

/**
 * Response from the bulk sync endpoint
 */
export interface BulkSyncResponse {
  results: BulkSyncItemResult[];
  summary: BulkSyncSummary;
  connectionError?: {
    errorCode: string;
    errorMessage: string;
  };
}

/**
 * QuickBooks bulk sync API methods
 */
export const quickBooksBulkApi = {
  /**
   * Bulk sync multiple expense reports to QuickBooks.
   *
   * Reports already synced are skipped (not failed).
   * Processing is sequential to respect QB rate limits.
   *
   * @param reportIds - Array of expense report UUIDs to sync
   * @returns Promise with bulk sync results and summary
   */
  async bulkSyncToQuickBooks(reportIds: string[]): Promise<ApiResponse<BulkSyncResponse>> {
    return apiClient.post<BulkSyncResponse>('/expense-reports/bulk/sync-quickbooks', { reportIds });
  },
};

/**
 * Fetch with authentication
 * A wrapper around fetch that automatically adds authentication headers
 * @param url - The URL to fetch (relative or absolute)
 * @param options - Fetch options
 * @returns The fetch response
 */
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  // Import dynamically to avoid circular dependency
  const { getAccessToken } = await import('./auth-store.js');

  const token = getAccessToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Prepend /api if the URL doesn't start with http or /api
  const fullUrl = url.startsWith('http') || url.startsWith('/api')
    ? url
    : `/api${url.startsWith('/') ? url : `/${url}`}`;

  return fetch(fullUrl, {
    ...options,
    headers,
  });
}
